import * as React from 'react';
import { Grid, Typography, styled, Table, TableHead, TableRow, TableCell, TableBody, useTheme } from '@mui/material';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import BillingSkeleton from 'src/pages/Settings/components/BillingInfo/BillingSkeleton/BillingSkeleton';
import { WhiteContainer } from 'src/style/common';
import Card from 'src/frontend-common/components/Card';
import { Subtitle } from 'src/pages/Settings/components/MyProfile/LoginHistory';
import { useGetPurchasesQuery } from 'src/redux/services/purchases';
import { useGetPlansQuery } from 'src/redux/services/plans';
import BillingProductsList from 'src/pages/Settings/components/BillingInfo/BillingProductsList';
import ReceiptsList from 'src/pages/Settings/components/BillingInfo/ReceiptsList';
import { Purchase } from 'src/dto/Credits';
import ButtonComponent from 'src/components/ButtonComponent';
import { BASE_URL } from 'src/redux/services/utils';
import AppkeyAutocomplete from 'src/components/AppkeyAutocomplete';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';

export const CardTitleContainer = styled(Card)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  boxShadow: '0px 0px 0px 0px white',
}));

const SubtitleColorSecondary = styled(Subtitle)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const SecondWhiteContainer = styled(WhiteContainer)(({ theme }) => ({
  marginTop: theme.spacing(4),
  boxShadow: '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
  padding: theme.spacing(0),
}));

const FirstBlockContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
}));

const TableCustomSize = styled(Table)(() => ({
  '& th, td': {
    width: '40%',
    '&:last-of-type': {
      width: '20%',
    },
  },
}));

const BillingInfo: React.FC = () => {
  const [selectedButton, setSelectedButton] = React.useState<string>('');
  const [stripeRequestSent, setstripeRequestSent] = React.useState<boolean>(false);
  const [selectedAppkey, setSelectedAppkey] = React.useState<{
    label: string;
    key: string;
  } | null>(null);
  const [selectedKeyReceipts, setSelectedKeyReceipts] = React.useState<Purchase[]>([]);

  const theme = useTheme();

  const { data: dataPlans, isFetching: isFetchingPlans, isError: isErrorPlans } = useGetPlansQuery();

  const { data: dataPurchase, isFetching: isFetchingPurchase, isError: isErrorPurchase } = useGetPurchasesQuery();

  const selectedKeyValues = React.useMemo(() => {
    if (dataPlans?.items && selectedAppkey) {
      // convert the object who has the selected appkey to an array of objs with label:key and value:rate pairs
      const planBySelectedAppKey = dataPlans.items.find((plan) => plan.app_key === selectedAppkey.key);
      if (planBySelectedAppKey === undefined) {
        return;
      }
      const planArray =
        planBySelectedAppKey &&
        Object.keys(planBySelectedAppKey).map((key) => ({
          label: key,
          value: planBySelectedAppKey[key as keyof typeof planBySelectedAppKey],
        }));
      const completePlanArray = planArray
        // filters objs by rate and create a new array with hardcoded values that extends the basic label-value
        .filter((plan) => plan.label.includes('rate'))
        /* eslint-disable array-callback-return */
        .map((el) => {
          switch (el.label) {
            case 'phone_rate':
              return {
                name: 'Phone',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference/phone-api',
              };
            case 'email_rate':
              return {
                name: 'Email',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference/email-api',
              };
            case 'ip_rate':
              return {
                name: 'Ip Address',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference/ip-api',
              };
            case 'device_rate':
              return {
                name: 'Device',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference/device-api',
              };
            case 'browser_rate':
              return {
                name: 'Browser',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference/browser-api',
              };
            case 'name_rate':
              return {
                name: 'Name',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference/name-api',
              };
            case 'tax_id_rate':
              return {
                name: 'Tax ID',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference',
              };
            case 'vat_code_rate':
              return {
                name: 'VAT code',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference',
              };
            case 'score_rate':
              return {
                name: 'Onboarding',
                rate: el.value,
                detailsLink: '',
                apiLink: 'https://docs.trustfull.com/reference/digital-score-api',
              };
          }
        });
      return completePlanArray;
    }
  }, [selectedAppkey]);

  const sendStripeRequest = async () => {
    const appkey = selectedAppkey?.key;
    const amount = selectedButton;
    const url = `${BASE_URL}/v1/stripe/session`;
    const token = localStorage.getItem('accessToken');
    const response: any = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? token.replace(/['"]+/g, '') : '',
      },
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify({
        app_key: appkey,
        amount: amount.replace(',', ''),
      }),
    });

    return response.json();
  };
  React.useEffect(() => {
    if (dataPurchase?.items && selectedAppkey) {
      const receiptsBySelectedKey = dataPurchase.items.filter((item) => item.app_key === selectedAppkey.key);
      const maxNumberOfReceipts = 5;
      setSelectedKeyReceipts(receiptsBySelectedKey.slice(0, maxNumberOfReceipts));
    }
  }, [dataPurchase?.items, selectedAppkey]);

  // removal of tax id and vat code from the table
  const partialSelectedKeyValues = selectedKeyValues?.filter((el) => el && el.name !== 'Fido Tax ID' && el.name !== 'Fido VAT code');

  return (
    <>
      {isFetchingPlans && <BillingSkeleton />}
      {!isFetchingPlans && isErrorPlans && <FailureScreen>There was an error when fetching results!</FailureScreen>}

      {!isFetchingPlans && !isErrorPlans && dataPlans?.items && (
        <>
          <WhiteContainer data-testid="plans-list">
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <CardTitleContainer>
                  <Typography variant="h5">Select Top-up amount</Typography>
                </CardTitleContainer>
              </Grid>
              {['100', '250', '500', '1,000', '2,500', '5,000'].map((el: string) => (
                <Grid item xs={12} md={6} key={el}>
                  <ButtonComponent
                    buttontype="big"
                    text={`${useGetCurrency()} ${el}`}
                    onClick={() => {
                      setSelectedButton(el);
                    }}
                    selected={selectedButton === el}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid item xs={12} md={3} style={{ marginTop: 24 }}>
              <ButtonComponent
                type="button"
                buttontype="mainaction"
                disabled={!selectedButton || stripeRequestSent}
                onClick={() => {
                  sendStripeRequest().then((data) => {
                    window.location.href = data.url;
                  });
                  setstripeRequestSent(true);
                }}
                text="Add funds"
              />
            </Grid>
          </WhiteContainer>
          <WhiteContainer style={{ padding: 0 }} container={true} data-testid="plans-list">
            <Grid item={true} xs={12} style={{ padding: theme.spacing(3) }}>
              <CardTitleContainer>
                <Typography variant="h5">Pricing</Typography>
                <SubtitleColorSecondary>In this section you can view the product pricing based on your current plan.</SubtitleColorSecondary>
                <AppkeyAutocomplete selectedAppkey={selectedAppkey} setSelectedAppkey={setSelectedAppkey} dataPlans={dataPlans} />
              </CardTitleContainer>
            </Grid>
            {partialSelectedKeyValues && <BillingProductsList list={partialSelectedKeyValues} />}
          </WhiteContainer>
          {!isFetchingPurchase && !isErrorPurchase && dataPurchase?.items && (
            <SecondWhiteContainer data-testid="purchase-list">
              <FirstBlockContainer>
                <Typography variant="h5">Payment History</Typography>
                <SubtitleColorSecondary>
                  You can view and download all your previous receipt here. If you’ve just made a payment, it may take a few hours for it to appear in the table
                  below.
                </SubtitleColorSecondary>
              </FirstBlockContainer>
              <TableCustomSize>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Total (incl. tax)</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <ReceiptsList list={selectedKeyReceipts} />
                </TableBody>
              </TableCustomSize>
            </SecondWhiteContainer>
          )}
        </>
      )}
    </>
  );
};

export default BillingInfo;
